import { QueryClient, QueryFunction } from '@tanstack/react-query';
import { captureError, addBreadcrumb } from '@/lib/sentry';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 30000,
};

// Request timeout in milliseconds
const REQUEST_TIMEOUT = 30000;
const CSRF_STORAGE_KEY = 'qf.csrfToken';
const CSRF_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43,128}$/;
const SESSION_EPOCH_PATTERN =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}):([0-9]+)$/;
let authorizationRevision = 0;
let acceptedSessionEpoch: { familyId: string; rotationSequence: number } | null = null;

/**
 * Calculate retry delay with exponential backoff and jitter
 */
function getRetryDelay(attemptIndex: number): number {
  const delay = Math.min(RETRY_CONFIG.baseDelay * Math.pow(2, attemptIndex), RETRY_CONFIG.maxDelay);
  // Add jitter to prevent thundering herd
  return delay + Math.random() * 1000;
}

/**
 * Determine if an error is retryable
 * - Retry 5xx server errors and network errors
 * - Retry 429 rate limit errors
 * - Don't retry 4xx client errors (except 429)
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= RETRY_CONFIG.maxRetries) return false;

  if (error instanceof Error) {
    const message = error.message;

    // Don't retry 4xx client errors (except 429 rate limit)
    if (/^4\d{2}:/.test(message) && !message.startsWith('429:')) {
      return false;
    }

    // Retry 5xx server errors
    if (/^5\d{2}:/.test(message)) {
      return true;
    }

    // Retry network errors and timeouts
    if (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('timeout') ||
      message.includes('Failed to fetch') ||
      message.includes('NetworkError')
    ) {
      return true;
    }

    // Retry rate limit errors
    if (message.startsWith('429:')) {
      return true;
    }
  }

  return false;
}

/**
 * Fetch with timeout wrapper
 */
async function fetchWithTimeout(url: string, options: RequestInit = {}): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const response = await fetch(url, {
      ...options,
      signal: options.signal || controller.signal,
    });
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Request timeout after ${REQUEST_TIMEOUT}ms`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (API_BASE && normalizedPath.startsWith(API_BASE)) {
    return normalizedPath;
  }
  return `${API_BASE}${normalizedPath}`;
}

function isSameOriginUrl(url: string): boolean {
  if (typeof window === 'undefined') return url.startsWith('/');
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

function responseStayedOnOrigin(requestUrl: string, response: Response): boolean {
  if (!isSameOriginUrl(requestUrl)) return true;
  if (response.redirected) return false;
  // Synthetic test responses may not expose a URL. Real browser responses do.
  return !response.url || isSameOriginUrl(response.url);
}

function joinQueryKey(queryKey: readonly unknown[]): string {
  const parts = queryKey
    .filter((segment): segment is string | number => ['string', 'number'].includes(typeof segment))
    .map(String);
  const rawPath = parts.join('/').replace(/\/+/g, '/');
  return rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
}

export function clearAuthenticatedBrowserState() {
  authorizationRevision += 1;
  acceptedSessionEpoch = null;
  setCsrfToken(null);
  // TanStack Query removal does not notify already-mounted QueryObservers.
  // Overwrite every observed value first so no mounted component can retain
  // the previous principal's protected payload after the cache is removed.
  queryClient.setQueriesData({}, () => null);
  queryClient.clear();
  queryClient.setQueryData(['/api/auth/user'], null);
}

export function clearAuthorizedQueryData() {
  authorizationRevision += 1;
  queryClient.setQueriesData(
    { predicate: (query) => query.queryKey[0] !== '/api/auth/user' },
    () => null,
  );
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== '/api/auth/user',
  });
}

function authorizationContext(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Record<string, unknown>;
  const required = ['id', 'username', 'role'];
  if (!required.every((field) => typeof candidate[field] === 'string')) return null;

  return ['id', 'username', 'role', 'organizationId', 'engagementId']
    .map((field) => (typeof candidate[field] === 'string' ? candidate[field] : ''))
    .join('\u001f');
}

function parseSessionEpoch(value: string): { familyId: string; rotationSequence: number } | null {
  const match = SESSION_EPOCH_PATTERN.exec(value);
  if (!match) return null;
  const rotationSequence = Number(match[2]);
  if (!Number.isSafeInteger(rotationSequence)) return null;
  return { familyId: match[1], rotationSequence };
}

export function acceptAuthenticatedSession(
  user: object,
  csrfToken: string,
  sessionEpoch: string,
): boolean {
  if (!CSRF_TOKEN_PATTERN.test(csrfToken)) return false;
  const nextEpoch = parseSessionEpoch(sessionEpoch);
  const nextContext = authorizationContext(user);
  if (!nextEpoch || nextContext === null) return false;

  if (
    acceptedSessionEpoch?.familyId === nextEpoch.familyId &&
    nextEpoch.rotationSequence < acceptedSessionEpoch.rotationSequence
  ) {
    return false;
  }

  const previousUser = queryClient.getQueryData(['/api/auth/user']);
  const previousContext = authorizationContext(previousUser);
  const familyChanged =
    acceptedSessionEpoch !== null && acceptedSessionEpoch.familyId !== nextEpoch.familyId;
  const rotationAdvanced =
    acceptedSessionEpoch?.familyId === nextEpoch.familyId &&
    nextEpoch.rotationSequence > acceptedSessionEpoch.rotationSequence;
  if ((previousContext !== null && previousContext !== nextContext) || familyChanged) {
    clearAuthorizedQueryData();
  } else if (rotationAdvanced) {
    authorizationRevision += 1;
  }

  acceptedSessionEpoch = nextEpoch;
  setCsrfToken(csrfToken);
  return true;
}

function handleAuthorizationFailure(url: string, res: Response, requestRevision: number): boolean {
  if (!isSameOriginUrl(url)) return false;
  if (requestRevision !== authorizationRevision) return false;
  if (res.status === 401) {
    clearAuthenticatedBrowserState();
    return true;
  } else if (res.status === 403) {
    clearAuthorizedQueryData();
    return true;
  }
  return false;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

function getCsrfToken(): string | null {
  if (typeof window === 'undefined') return null;
  const token = window.sessionStorage.getItem(CSRF_STORAGE_KEY);
  if (token && CSRF_TOKEN_PATTERN.test(token)) return token;
  if (token !== null) window.sessionStorage.removeItem(CSRF_STORAGE_KEY);
  return null;
}

function setCsrfToken(token: string | null) {
  if (typeof window === 'undefined') return;
  if (token && CSRF_TOKEN_PATTERN.test(token)) {
    window.sessionStorage.setItem(CSRF_STORAGE_KEY, token);
  } else {
    window.sessionStorage.removeItem(CSRF_STORAGE_KEY);
  }
}

function clearCsrfAfterLogout(url: string) {
  if (!isSameOriginUrl(url)) return;
  if (url.endsWith('/auth/logout')) {
    setCsrfToken(null);
  }
}

async function readApiResponse(url: string, res: Response): Promise<unknown> {
  const contentType = res.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    clearCsrfAfterLogout(url);
    return res;
  }

  const payload: unknown = await res.json();
  clearCsrfAfterLogout(url);
  return payload;
}

export async function apiRequest(
  method: string,
  path: string,
  data?: unknown | undefined,
): Promise<unknown> {
  const guarded = await apiFetchResponse(method, path, data);
  guarded.assertAuthorizationCurrent();
  await throwIfResNotOk(guarded.response);
  const payload = await readApiResponse(guarded.url, guarded.response);
  guarded.assertAuthorizationCurrent();
  return payload;
}

export interface GuardedApiResponse {
  response: Response;
  url: string;
  assertAuthorizationCurrent: () => void;
}

export async function apiFetchResponse(
  method: string,
  path: string,
  data?: unknown | undefined,
  signal?: AbortSignal,
): Promise<GuardedApiResponse> {
  const url = buildUrl(path);
  const normalizedMethod = method.toUpperCase();
  const sameOrigin = isSameOriginUrl(url);
  const requestAuthorizationRevision = authorizationRevision;
  const headers: Record<string, string> = data ? { 'Content-Type': 'application/json' } : {};
  const csrfToken = normalizedMethod !== 'GET' && sameOrigin ? getCsrfToken() : null;
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }
  const res = await fetchWithTimeout(url, {
    method: normalizedMethod,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: sameOrigin ? 'include' : 'omit',
    redirect: sameOrigin ? 'error' : 'follow',
    signal,
  });
  if (!responseStayedOnOrigin(url, res)) {
    throw new Error('Protected API response crossed an origin boundary');
  }
  const revisionBeforeAuthorizationFailure = authorizationRevision;
  const authorizationFailureApplied = handleAuthorizationFailure(
    url,
    res,
    requestAuthorizationRevision,
  );
  const staleAuthorizationResponse =
    sameOrigin &&
    requestAuthorizationRevision !== revisionBeforeAuthorizationFailure &&
    !authorizationFailureApplied;
  const responseAuthorizationRevision = authorizationRevision;
  return {
    response: res,
    url,
    assertAuthorizationCurrent: () => {
      if (
        staleAuthorizationResponse ||
        (sameOrigin && responseAuthorizationRevision !== authorizationRevision)
      ) {
        throw new Error('Authorization state changed while the request was in flight');
      }
    },
  };
}

type UnauthorizedBehavior = 'returnNull' | 'throw';
export const getQueryFn: <T>(options: { on401: UnauthorizedBehavior }) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey, signal }) => {
    const guarded = await apiFetchResponse('GET', joinQueryKey(queryKey), undefined, signal);
    const { response: res } = guarded;
    guarded.assertAuthorizationCurrent();

    if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    const payload = await res.json();
    guarded.assertAuthorizationCurrent();
    return payload;
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'returnNull' }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: shouldRetry,
      retryDelay: getRetryDelay,
    },
    mutations: {
      retry: shouldRetry,
      retryDelay: getRetryDelay,
      onError: (error) => {
        console.warn('Mutation error:', error);

        // Add breadcrumb for context
        addBreadcrumb({
          category: 'mutation',
          message: 'Mutation failed',
          level: 'error',
        });

        // Report to Sentry
        if (error instanceof Error) {
          captureError(error, { type: 'mutation' });
        }
      },
    },
  },
});
